import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import { IntroPageEditor } from '../../components/IntroPageEditor';
import { projectIntroApi } from '../../api/endpoints';
import { getApiErrorMessage, resolveApiAssetUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import type { IntroPageType } from '../../types';

/**
 * Ảnh chèn vào editor phải là URL tuyệt đối: thẻ <img> không đi qua axios
 * baseURL, nên URL tương đối "/api/..." phân giải theo origin trang web
 * (Pages) thay vì Worker (API) và vỡ khi web/API khác domain ở production
 * (xem resolveApiAssetUrl trong api/client.ts).
 */
export function toAbsoluteIntroImageUrl(url: string): string {
  return resolveApiAssetUrl(url) ?? url;
}

const TAB_TYPES: { label: string; type: IntroPageType }[] = [
  { label: 'Giới thiệu', type: 'demo' },
  { label: 'Hướng dẫn sử dụng', type: 'guide' },
];

/**
 * Soạn nội dung trang giới thiệu/hướng dẫn công khai của 1 dự án.
 * Gắn vào /admin/projects/:projectId/intro, cùng cấp PhaseManagement.
 */
export function ProjectIntroPages(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const [tab, setTab] = useState<IntroPageType>('demo');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const page = await projectIntroApi.getAdmin(projectId, tab);
      setTitle(page.title);
      setContent(page.content_html);
      setIsPublished(page.is_published === 1);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải nội dung'), 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await projectIntroApi.upsert(projectId, tab, { title, content_html: content, is_published: isPublished });
      notify('Đã lưu', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu nội dung'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const publicPath = tab === 'demo' ? 'gioi-thieu' : 'huong-dan';

  if (!projectId) return <Box />;

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v: IntroPageType) => setTab(v)} sx={{ mb: 2 }}>
        {TAB_TYPES.map((t) => (
          <Tab key={t.type} label={t.label} value={t.type} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2}>
          <TextField label="Tiêu đề" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
          <IntroPageEditor
            value={content}
            onChange={setContent}
            onUploadImage={async (file) => {
              const res = await projectIntroApi.uploadImage(projectId, tab, file);
              return toAbsoluteIntroImageUrl(res.url);
            }}
          />
          <FormControlLabel
            control={<Switch checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />}
            label="Xuất bản (khách hàng xem được)"
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Đang lưu…' : 'Lưu'}
            </Button>
            <Button variant="outlined" onClick={() => window.open(`/${publicPath}/${projectId}`, '_blank')}>
              Xem thử
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
